import { useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { RewardProgramDto, RewardWidgetStatus } from '@pages/RewardsHub/RewardHubDto'
import { useRewardCadenceCalculation } from '@pages/RewardsHub/hooks/useRewardCadenceCalculation'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { BadgeType } from '@ui/components/Badge/Badge'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { useLuckyDraw } from '@shared/components/LuckyDraw/useLuckyDraw'

const REWARD_STATUS_COLORS: Record<number, BadgeType> = {
  1: 'blue',
  2: 'purple',
  3: 'green',
  4: 'yellow',
}

export const useRewardProgramWidget = (rewardProgram: RewardProgramDto) => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { openLuckyDrawModal } = useLuckyDraw()
  const locale = useSessionLanguage()

  const { data: rewardProgramDetails, isLoading: rewardProgramDetailsLoading } = useQuery({
    queryKey: ['rewardProgramDetails', rewardProgram.id],
    queryFn: () => apiClient.getRewardProgramDetails(rewardProgram.id),
  })

  const { data: rewardProgramEligibilityCheck, isLoading: rewardProgramEligibilityCheckLoading } =
    useQuery({
      queryKey: ['rewardProgramEligibilityCheck', rewardProgram.id, locale],
      queryFn: () => apiClient.getRewardProgramEligibilityCheck(rewardProgram.id, locale),
    })

  const { timeRemaining } = useRewardCadenceCalculation(
    rewardProgramDetails?.rewardProgramInfo.resetCadence.id ?? 0
  )

  const [isClaiming, setIsClaiming] = React.useState(false)

  const isLoading = rewardProgramDetailsLoading || rewardProgramEligibilityCheckLoading

  const widgetStateId = rewardProgramEligibilityCheck?.rewardWidgetState.id
  const isProgramInProgress = widgetStateId === RewardWidgetStatus.IN_PROGRESS
  const isReadyToClaim = widgetStateId === RewardWidgetStatus.READY_TO_CLAIM
  const isRewardClaimed = widgetStateId === RewardWidgetStatus.CLAIMED

  const rewardToClaimId = rewardProgramEligibilityCheck?.rewardProgramTier?.id

  const claimableTier = rewardProgramDetails?.rewardProgramInfo.tiers.find(
    (tier) => tier.id === rewardToClaimId
  )

  const claimableTierIndex = rewardProgramDetails?.rewardProgramInfo.tiers.findIndex(
    (tier) => tier.id === String(rewardToClaimId)
  )

  const spinsClaimed = rewardProgramDetails?.spins ?? []
  const areSpinsEmpty = spinsClaimed.length === 0
  const hasUnusedSpins = isRewardClaimed && !areSpinsEmpty
  const shouldShowClaimButton = (isRewardClaimed && !areSpinsEmpty) || isReadyToClaim

  const claimButtonLabel = hasUnusedSpins
    ? t('RewardsHub.Spin the Wheel')
    : isReadyToClaim
    ? claimableTier?.callToActionText
    : t('RewardsHub.Claim Reward')

  const badgeType = widgetStateId ?? -1
  const badgeColor: BadgeType = REWARD_STATUS_COLORS[badgeType] ?? 'black'

  const badgeLabel = rewardProgramEligibilityCheck?.rewardWidgetState.name

  const handleClick = async () => {
    if (hasUnusedSpins) {
      openLuckyDrawModal()
      return
    }
    if (rewardToClaimId && isReadyToClaim) {
      setIsClaiming(true)
      try {
        await apiClient.postClaimReward(rewardToClaimId, undefined)
        await queryClient.invalidateQueries({
          queryKey: ['rewardProgramDetails', rewardProgram.id],
        })
        await queryClient.invalidateQueries({
          queryKey: ['rewardProgramEligibilityCheck', rewardProgram.id],
        })
      } finally {
        setIsClaiming(false)
      }
    }
  }

  return {
    isLoading,
    rewardProgramDetails,
    rewardProgramEligibilityCheck,
    timeRemaining,
    isClaiming,
    isProgramInProgress,
    shouldShowClaimButton,
    claimButtonLabel,
    badgeColor,
    badgeLabel,
    claimableTierIndex,
    handleClick,
  }
}
